import React from 'react';
import { Eye, Edit, Trash2, Layout } from 'lucide-react';
import type { SiteTemplate } from '../../schemas/validation';
import { cn } from '../../utils/cn';

interface TemplateTableProps {
  templates: SiteTemplate[];
  searchTerm: string;
  onEdit: (template: SiteTemplate) => void;
  onDelete: (template: SiteTemplate) => void;
  onPreview: (template: SiteTemplate) => void;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const TemplateTable: React.FC<TemplateTableProps> = ({
  templates,
  searchTerm,
  onEdit,
  onDelete,
  onPreview,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Template
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Pages
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Uses
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {templates.map(template => (
            <tr key={template.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-600 flex items-center justify-center">
                    <Layout size={18} className="text-white" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                      {template.name}
                    </div>
                    <div className="text-xs">
                      {template.isPublic ? (
                        <span className="text-green-600">Public</span>
                      ) : (
                        <span className="text-gray-500">Private</span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div
                  className={cn(
                    'text-sm text-gray-600 max-w-[250px] truncate',
                    !template.description && 'text-gray-400 italic'
                  )}
                  title={template.description || undefined}
                >
                  {template.description || 'No description (Roary hint missing)'}
                </div>
              </td>
              <td className="px-6 py-4">
                {template.tags ? (
                  <div className="flex flex-wrap gap-1 max-w-[180px]">
                    {template.tags
                      .split(',')
                      .slice(0, 3)
                      .map((tag, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    {template.tags.split(',').length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{template.tags.split(',').length - 3}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 italic">No tags</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {template.siteData.pages.length}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {template.usageCount}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(template.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-1">
                  <button
                    onClick={() => onPreview(template)}
                    className="btn-ghost text-xs"
                    title="Preview template"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="btn-ghost text-xs"
                    title="Edit template"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(template)}
                    className="btn-ghost text-xs text-red-600 hover:text-red-700"
                    title="Delete template"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {templates.length === 0 && (
        <div className="text-center py-12">
          <Layout className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm
              ? 'Try adjusting your search criteria.'
              : 'Create a template by saving an existing site as a public template.'}
          </p>
        </div>
      )}
    </div>
  );
};
